import { useMemo } from "react";
import PerformanceIcon from "../../../assets/PerformanceIcon";
import ProgressIcon from "../../../assets/ProgressIcon";
import PromptsIcon from "../../../assets/PromptsIcon";
import UsageIcon from "../../../assets/UsageIcon";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import { AdminReportBlock, AdminReportBlockConfig } from "./AdminReportBlock";
import { useAdminReportsFilters } from "./useAdminReportsFilters";
import { getReportLevel, reportSubLevelName } from "./adminReportsUtil";
import { ReportLevel } from "./adminReportsApiSlice";
import { DistrictSchoolFilters } from "./DistrictSchoolFilters";

interface Props {
	canViewAdminReports: boolean;
}

export default function AdminLevelReports(props: Props) {
	const { role, schools } = useAppSelector((state) => state.user);
	const {
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const reportBlocks: AdminReportBlockConfig[] = useMemo(() => {
		const reportLevel = getReportLevel(districtFilter, schoolFilter);
		const reportSubLevel = reportSubLevelName(reportLevel);
		return [
			{
				heading: "Usage",
				Icon: UsageIcon,
				iconLink: "/adminReports/usageSummary",
				links: [
					{
						text: `${reportLevel} Usage Summary`,
						link: "/adminReports/usageSummary",
					},
					{
						text: `${reportLevel} Essays And Drafts Summary by ${reportSubLevel}`,
						link: "/adminReports/essaysAndDrafts",
					},
					{
						text: `${reportLevel} Draft Usage Over Time by ${reportSubLevel}`,
						link: "/adminReports/draftUsage",
					},
					{
						text: `${reportLevel} Peer Review Usage by ${reportSubLevel}`,
						link: "/adminReports/reviewsUsage",
					},
					{
						text: `${reportLevel} Lesson Usage by ${reportSubLevel}`,
						link: "/adminReports/lessonUsage",
					},
					{
						text: `${reportLevel} Lesson Usage by Category`,
						link: "/adminReports/lessonUsageByCategory",
					},
				],
			},
			{
				heading: "Performance",
				Icon: PerformanceIcon,
				iconLink: "/adminReports/holisticAndTraitScores",
				links: [
					{
						text: `${reportLevel} Holistic and Trait Scores by ${reportSubLevel}`,
						link: "/adminReports/holisticAndTraitScores",
					},
					...(reportLevel === ReportLevel.School
						? [
								{
									text: `School Holistic and Trait Scores by Genre`,
									link: "/adminReports/holisticAndTraitScoresByGenre",
								},
							]
						: []),
					{
						text: `${reportLevel} Average Genre Scores by ${reportSubLevel}`,
						link: "/adminReports/scoresByGenre",
					},
				],
			},
			{
				heading: "Prompts",
				Icon: PromptsIcon,
				iconLink: "/adminReports/usageAndPerformanceByPrompt",
				links: [
					{
						text: `${reportLevel} Usage and Performance by Prompt`,
						link: "/adminReports/usageAndPerformanceByPrompt",
					},
					{
						text: `${reportLevel} Prompt Usage and Performance by ${reportSubLevel}`,
						link: "/adminReports/promptUsageAndPerformanceByLevel",
					},
				],
			},
			{
				heading: "Progress",
				Icon: ProgressIcon,
				iconLink: "/adminReports/averageScores",
				links: [
					{
						text: `${reportLevel} Average Monthly Scores by ${reportSubLevel}`,
						link: "/adminReports/averageScores",
					},
				],
			},
		];
	}, [districtFilter, schoolFilter]);

	return (
		<section>
			{props.canViewAdminReports ? (
				<>
					<div
						className={`${role === UserRole.SchoolAdmin && (schools?.length ?? 0) <= 1 ? "" : "min-h-[88px]"} mt-4`}>
						<DistrictSchoolFilters
							districtOptions={districtOptions}
							schoolOptions={schoolOptions}
							districtFilter={districtFilter}
							schoolFilter={schoolFilter}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
							labelClasses="!text-lg"
							listBoxClasses="w-80"
						/>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
						{reportBlocks.map((b, index) => {
							return (
								<AdminReportBlock
									block={b}
									key={index}
									reportLevel={getReportLevel(districtFilter, schoolFilter)}
								/>
							);
						})}
					</div>
				</>
			) : (
				<div className="mt-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
					<p className="text-gray-700 text-center">
						Reports are not enabled. Please contact the MI Write team to enable reports
						for you.
					</p>
				</div>
			)}
		</section>
	);
}
